import Module from './Module';
import store from '../../store';
import { BUTTONS_VISIBILITY, USER_TYPES } from '../../constants';

class Location {
    constructor(location) {
        // merge properties
        Object.assign(this, location);

        if (this.hasModules()) {
            this.modules = this.modules.map(module => new Module(module));
        }

        this.initButtonsVisibility = this.buttons_visibility;
    }

    getButtonsVisibility() {
        return (
            this.buttons_visibility === BUTTONS_VISIBILITY.TO_ALL ||
            (this.buttons_visibility === BUTTONS_VISIBILITY.TO_SU &&
                [USER_TYPES.USER_ADMIN, USER_TYPES.SUPER_USER].includes(store.state.user.user.type))
        );
    }

    setButtonsVisibility(val) {
        this.buttons_visibility = val;
        if (this.hasModules()) {
            this.modules.forEach(module => module.setButtonsVisibility(val));
        }
        if (this.hasChildren()) {
            this.children.forEach(location => location.setButtonsVisibility(val));
        }
    }

    isButtonsVisibilityChanged() {
        return (
            this.initButtonsVisibility !== this.buttons_visibility ||
            (this.hasModules() && this.modules.some(module => module.isButtonsVisibilityChanged())) ||
            (this.hasChildren() && this.children.some(location => location.isButtonsVisibilityChanged()))
        );
    }

    getButtonsVisibilityChanges() {
        return {
            location_id: this.location_id,
            buttons_visibility: this.buttons_visibility,
            modules: this.hasModules() ? this.modules.map(m => m.getButtonsVisibilityChanges()) : null,
        };
    }

    isButtonsVisibilityNotToAll() {
        return (
            this.buttons_visibility !== BUTTONS_VISIBILITY.TO_ALL ||
            (this.hasModules() && this.modules.some(m => m.isButtonsVisibilityNotToAll())) ||
            (this.hasChildren() && this.children.some(m => m.isButtonsVisibilityNotToAll()))
        );
    }

    hasModules() {
        return !!this.modules && this.modules.length > 0;
    }

    hasChildren() {
        return !!this.children && this.children.length > 0;
    }

    hasListenInOption() {
        return (
            (this.hasModules() && this.modules.some(module => module.hasListenInOption())) ||
            (this.hasChildren() && this.children.some(location => location.hasListenInOption()))
        );
    }

    getListenInStatus() {
        // Off status is more important than On;
        // if has modules with option and one of them is switched off or the same for children, then it's switched off
        // it's turned on if has option and every module/child with option is turned on
        return (
            this.hasListenInOption() &&
            (!this.hasModules() ||
                this.modules.every(m => !m.hasListenInOption() || m.getListenInStatus())) &&
            (!this.hasChildren() || this.children.every(l => !l.hasListenInOption() || l.getListenInStatus()))
        );
    }

    setListenInStatus(val) {
        if (this.hasModules()) {
            this.modules.forEach(module => module.setListenInStatus(val));
        }

        if (this.hasChildren()) {
            this.children.forEach(location => location.setListenInStatus(val));
        }
    }

    toggleListenInStatus() {
        this.setListenInStatus(!this.getListenInStatus());
    }

    hasUpgradeOption() {
        return (
            (this.hasModules() && this.modules.some(module => module.hasUpgradeOption())) ||
            (this.hasChildren() && this.children.some(location => location.hasUpgradeOption()))
        );
    }

    getUpgradeStatus() {
        // On status is more important than Off;
        return (
            (this.hasModules() &&
                this.modules.some(module => module.hasUpgradeOption() && module.getUpgradeStatus())) ||
            (this.hasChildren() &&
                this.children.some(location => location.hasUpgradeOption() && location.getUpgradeStatus()))
        );
    }

    setUpgradeStatus(val) {
        if (this.hasModules()) {
            this.modules.forEach(module => module.setUpgradeStatus(val));
        }

        if (this.hasChildren()) {
            this.children.forEach(location => location.setUpgradeStatus(val));
        }
    }

    toggleUpgradeStatus() {
        this.setUpgradeStatus(!this.getUpgradeStatus());
    }

    hasEnabledOption() {
        return (
            (this.hasModules() && this.modules.some(module => module.hasEnabledOption())) ||
            (this.hasChildren() && this.children.some(location => location.hasEnabledOption()))
        );
    }

    getEnabledStatus() {
        // Disabled status is more important than enabled;
        return (
            this.hasEnabledOption() &&
            (!this.hasModules() ||
                this.modules
                    .filter(module => module.getButtonsVisibility())
                    .every(module => module.getEnabledStatus())) &&
            (!this.hasChildren() ||
                this.children.every(location => !location.hasEnabledOption() || location.getEnabledStatus()))
        );
    }

    setEnabledStatus(val) {
        if (this.hasModules()) {
            this.modules.forEach(module => module.setEnabledStatus(val));
        }

        if (this.hasChildren()) {
            this.children.forEach(location => location.setEnabledStatus(val));
        }
    }

    toggleEnabledStatus() {
        this.setEnabledStatus(!this.getEnabledStatus());
    }

    isRoot() {
        return Number(this.type) === 1;
    }

    hasOwnAcousticOption() {
        return this.hasModules() && this.modules.some(module => module.hasAcousticOption());
    }

    hasAcousticOption() {
        return (
            this.hasOwnAcousticOption() ||
            (this.hasChildren() && this.children.some(location => location.hasAcousticOption()))
        );
    }

    getAcousticStatus() {
        // Disabled status is more important than enabled;
        return (
            this.hasOwnAcousticOption() &&
            this.modules.every(module => !module.hasAcousticOption() || module.getAcousticStatus())
        );
    }

    setAcousticStatus(val) {
        if (this.hasModules()) {
            this.modules
                .filter(module => module.hasAcousticOption())
                .forEach(module => module.setAcousticStatus(val));
        }
    }

    getActivationTime() {
        // get any module with enabled acoustic function or the first one
        return this.hasModules()
            ? (this.modules.find(module => module.hasAcousticOption()) || this.modules[0]).getDuration()
            : undefined;
    }

    setActivationTime(val) {
        if (this.hasModules()) {
            this.modules.forEach(module => module.setDuration(val));
        }
    }

    getSoundLevel() {
        // get any module with enabled acoustic function or the first one
        return this.hasModules()
            ? (this.modules.find(module => module.hasAcousticOption()) || this.modules[0]).getThreshold()
            : undefined;
    }

    setSoundLevel(val) {
        if (this.hasModules()) {
            this.modules.forEach(module => module.setThreshold(val));
        }
    }

    isChanged() {
        return this.hasModules() && this.modules.some(module => module.isChanged());
    }

    // can be used for disabling save button
    hasNestedChanges() {
        return (
            this.isChanged() ||
            (this.hasChildren() && this.children.some(location => location.hasNestedChanges()))
        );
    }

    // Converts tree into single-level array
    flattenNestedChildren() {
        return this.hasChildren()
            ? this.children.reduce((memo, location) => memo.concat(location.flattenNestedChildren()), [this])
            : [this];
    }

    getCountModules() {
        return this.hasModules() ? this.modules.length : 0;
    }

    getSaveData() {
        return {
            location_id: this.location_id,
            modules: this.hasModules() ? this.modules.map(m => m.getSaveData()) : null,
        };
    }
}

export default Location;
